import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getPortalContainer } from '../../../lib/portalContainer'
import * as s from '../Table.css'

const VIEWPORT_PAD = 8
const GAP = 4

export type TablePopoverAlign = 'start' | 'end'

export function TablePopover({
  anchorRef,
  open,
  onClose,
  align = 'start',
  className = '',
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>
  open: boolean
  onClose: () => void
  align?: TablePopoverAlign
  className?: string
  children: React.ReactNode
}) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }

    const updatePosition = () => {
      const anchor = anchorRef.current
      const popover = popoverRef.current
      if (!anchor) return

      const rect = anchor.getBoundingClientRect()
      const width = popover?.offsetWidth ?? 200
      const height = popover?.offsetHeight ?? 0

      let left = align === 'end' ? rect.right - width : rect.left
      let top = rect.bottom + GAP

      if (left + width > window.innerWidth - VIEWPORT_PAD) {
        left = window.innerWidth - width - VIEWPORT_PAD
      }
      if (left < VIEWPORT_PAD) left = VIEWPORT_PAD

      if (top + height > window.innerHeight - VIEWPORT_PAD) {
        top = Math.max(VIEWPORT_PAD, rect.top - height - GAP)
      }

      setPosition({ top, left })
    }

    updatePosition()
    const raf = requestAnimationFrame(updatePosition)

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, align, anchorRef])

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      const anchor = anchorRef.current
      if (
        popoverRef.current?.contains(target) ||
        anchor?.contains(target)
      ) {
        return
      }
      onClose()
    }

    document.addEventListener('mousedown', handlePointerDown, true)
    return () => document.removeEventListener('mousedown', handlePointerDown, true)
  }, [open, onClose, anchorRef])

  if (!open) return null

  const popoverClass = [s.popover, s.popoverPortaled, className].filter(Boolean).join(' ')

  return createPortal(
    <div
      ref={popoverRef}
      className={popoverClass}
      style={
        position
          ? { top: position.top, left: position.left }
          : { top: 0, left: 0, visibility: 'hidden' }
      }
      role="dialog"
    >
      {children}
    </div>,
    getPortalContainer(),
  )
}
